import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import CheckoutPopup from '../components/CheckoutPopup';
import StoreNavbar from '../components/StoreNavbar';
import { useLanguage } from '../components/Navbar';

// Styled Components
const CartPageContainer = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  padding-top: 0;
`;

// Navigation Bar (Store'dan kopyalandı)
const NavigationBar = styled.nav`
  position: relative;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid #f5f5f5;
  padding: 1rem 0;
  transition: all 0.3s ease;
  margin-top: 0;
`;

const NavContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 300;
  letter-spacing: -0.02em;
  color: #1a1a1a;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: #d4af37;
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    text-align: center;
    width: 100%;
  }
`;

const NavCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex: 1;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: row;
    gap: 0.5rem;
    width: 100%;
    justify-content: space-between;
  }
`;

const SearchBar = styled.div`
  position: relative;
  width: 300px;
  
  @media (max-width: 1024px) {
    width: 200px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 25px;
  background: #f5f5f5;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
    color: #888888;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  background-image: url('/images/logos/search.webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  filter: brightness(0.5);
`;

const CategoryDropdown = styled.div`
  position: relative;
`;

const CategoryDropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 200px;
  display: ${props => props.isOpen ? 'block' : 'none'};
  margin-top: 0.5rem;
`;

const CategoryDropdownItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  color: #1a1a1a;
  
  &:hover {
    background: #f5f5f5;
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const CategoryDropdownButton = styled.button`
  background: none;
  border: none;
  color: #1a1a1a;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #f5f5f5;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.4rem 0.6rem;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
    justify-content: flex-end;
    width: auto;
  }
`;

const OrdersButton = styled.button`
  background: transparent;
  border: none;
  outline: none;
  color: #1a1a1a;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    background: #d4af37;
    color: #ffffff;
    transform: translateY(-2px);
  }
  
  &:focus {
    outline: none;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.8rem;
    font-size: 0.75rem;
  }
`;

const CartButton = styled.button`
  position: relative;
  background: none;
  border: none;
  outline: none;
  color: #1a1a1a;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  
  &:focus {
    outline: none;
  }
  
  transition: all 0.3s ease;
  width: 24px;
  height: 24px;
  background-image: url('/images/logos/pngwing.com (11).webp');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  
  &:hover {
    background-color: #f5f5f5;
  }
`;

const CartBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #d4af37;
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageHeader = styled.div`
  padding: 2rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #ffffff;
  font-family: serif;
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: #cfcfcf;
  font-weight: 300;
`;

const CartContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CartItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr auto;
  gap: 1rem;
  background: #1a1a1a;
  border-radius: 8px;
  padding: 1rem;
  align-items: center;
  
  @media (max-width: 576px) {
    grid-template-columns: 80px 1fr;
    gap: 0.5rem;
  }
`;

const ItemImage = styled.img`
  width: 100%;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ItemName = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  color: #ffffff;
`;

const ItemPrice = styled.div`
  font-size: 1rem;
  color: #d4af37;
`;

const ItemActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  
  @media (max-width: 576px) {
    grid-column: span 2;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 0.5rem;
  }
`;


const RemoveButton = styled.button`
  background: #333333;
  color: #ffffff;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #444444;
  }
`;

const CartSummary = styled.div`
  background: #1a1a1a;
  border-radius: 8px;
  padding: 1.5rem;
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const SummaryTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #ffffff;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #cfcfcf;
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
`;

const CheckoutButton = styled.button`
  width: 100%;
  background: #d4af37;
  color: #0d0d0d;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1.5rem;
  
  &:hover {
    background: #b8941f;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #666666;
    cursor: not-allowed;
    transform: none;
  }
`;

const ContinueShoppingButton = styled.button`
  width: 100%;
  background: transparent;
  color: #d4af37;
  border: 1px solid #d4af37;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #cfcfcf;
`;

const EmptyCartTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const EmptyCartText = styled.p`
  font-size: 1rem;
  margin-bottom: 2rem;
  color: #cfcfcf;
`;

const CargoSection = styled.div`
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const CargoTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
`;

const CargoWarning = styled.div`
  font-size: 0.75rem;
  color: #ff6b6b;
  margin-bottom: 0.75rem;
  font-weight: 500;
`;

const CargoOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CargoOption = styled.div<{ selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: ${props => props.selected ? 'rgba(212, 175, 55, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
  border: 2px solid ${props => props.selected ? '#d4af37' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
    background: rgba(212, 175, 55, 0.05);
  }
`;

const CargoInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const CargoName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
`;

const CargoDelivery = styled.span`
  font-size: 0.75rem;
  color: #cfcfcf;
  margin-top: 0.25rem;
`;

const CargoPrice = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #d4af37;
`;

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const { cartItems, cartTotal, cartCount, loading, error, removeFromCart, clearCart } = useCart();
  const [isCheckoutPopupOpen, setIsCheckoutPopupOpen] = useState(false);
  const [selectedCargo, setSelectedCargo] = useState<string>('');
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);


  const handleRemoveItem = (itemId: number) => {
    removeFromCart(itemId);
  };

  // Sepetteki ürün kategorilerini kontrol et
  const hasShoes = () => {
    return cartItems.some((item: any) => item.category === 'Ayakkabı');
  };

  const hasClothing = () => {
    return cartItems.some((item: any) => item.category === 'Giyim');
  };

  // Kargo ücretini hesapla
  const getCargoPrice = (cargoCompany: string) => {
    if (!cargoCompany) return 0;
    
    const cargoPrices: { [key: string]: { shoes: number; clothing: number } } = {
      'Aras Kargo': { shoes: 200, clothing: 150 },
      'Sürat Kargo': { shoes: 450, clothing: 350 },
      'DHL E-COMMERCE': { shoes: 70, clothing: 70 }
    };
    
    const prices = cargoPrices[cargoCompany];
    if (!prices) return 0;
    
    // Eğer ayakkabı varsa ayakkabı fiyatı, yoksa giyim fiyatı
    if (hasShoes()) {
      return prices.shoes;
    } else if (hasClothing()) {
      return prices.clothing;
    }
    
    return prices.clothing; // Default
  };

  const getShippingCost = () => {
    return getCargoPrice(selectedCargo);
  };

  const getTotalWithShipping = () => {
    return cartTotal + getShippingCost();
  };

  const handleCheckout = () => {
    if (!selectedCargo) {
      alert('Lütfen bir kargo firması seçin');
      return;
    }
    // Kargo bilgilerini localStorage'a kaydet
    localStorage.setItem('selectedCargo', selectedCargo);
    localStorage.setItem('cargoPrice', getShippingCost().toString());
    // Popup aç
    setIsCheckoutPopupOpen(true);
  };

  const handleContinueShopping = () => {
    navigate('/products');
  };

  const renderNavBar = () => (
    <NavigationBar>
      <NavContainer>
        <Logo onClick={() => navigate('/store')}>STORE</Logo>
        
        <NavCenter>
          <SearchBar>
            <SearchIcon></SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search items..."
              readOnly
            />
          </SearchBar>
        </NavCenter>
        
        <NavRight>
          <OrdersButton onClick={() => navigate('/')}>
            {language === 'tr' ? 'Ana Sayfa' : 'Home'}
          </OrdersButton>
          <OrdersButton onClick={() => navigate('/orders')}>
            {language === 'tr' ? 'Siparişlerim' : 'My Orders'}
          </OrdersButton>
          <div style={{ position: 'relative' }}>
            <OrdersButton onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}>
              🌐 {language.toUpperCase()}
            </OrdersButton>
            {isLanguageDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                overflow: 'hidden',
                minWidth: '140px',
                zIndex: 1000
              }}>
                <button 
                  onClick={(e) => { e.stopPropagation(); setLanguage('tr'); setIsLanguageDropdownOpen(false); }} 
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    border: 'none',
                    background: language === 'tr' ? '#d4af37' : 'white',
                    color: language === 'tr' ? 'white' : '#1a1a1a',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: language === 'tr' ? '600' : '400',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => { if (language !== 'tr') e.currentTarget.style.background = '#f5f5f5'; }}
                  onMouseLeave={(e) => { if (language !== 'tr') e.currentTarget.style.background = 'white'; }}
                >
                  🇹🇷 Türkçe
                </button>
                <button 
                  onClick={(e) => { e.stopPropagation(); setLanguage('en'); setIsLanguageDropdownOpen(false); }} 
                  style={{
                    width: '100%',
                    padding: '12px 20px',
                    border: 'none',
                    background: language === 'en' ? '#d4af37' : 'white',
                    color: language === 'en' ? 'white' : '#1a1a1a',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: language === 'en' ? '600' : '400',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => { if (language !== 'en') e.currentTarget.style.background = '#f5f5f5'; }}
                  onMouseLeave={(e) => { if (language !== 'en') e.currentTarget.style.background = 'white'; }}
                >
                  🇬🇧 English
                </button>
              </div>
            )}
          </div>
          <CartButton onClick={() => navigate('/cart')}>
            {cartCount > 0 && <CartBadge>{cartCount}</CartBadge>}
          </CartButton>
        </NavRight>
      </NavContainer>
    </NavigationBar>
  );

  if (loading) {
    return (
      <CartPageContainer>
        {renderNavBar()}
        <Container>
          <PageHeader>
            <PageTitle>{language === 'tr' ? 'Sepetiniz' : 'Your Cart'}</PageTitle>
            <PageSubtitle>{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</PageSubtitle>
          </PageHeader>
        </Container>
      </CartPageContainer>
    );
  }

  if (error) {
    return (
      <CartPageContainer>
        {renderNavBar()}
        <Container>
          <PageHeader>
            <PageTitle>{language === 'tr' ? 'Sepetiniz' : 'Your Cart'}</PageTitle>
            <PageSubtitle>{language === 'tr' ? 'Bir hata oluştu:' : 'An error occurred:'} {error}</PageSubtitle>
          </PageHeader>
        </Container>
      </CartPageContainer>
    );
  }

  if (!cartItems.length) {
    return (
      <CartPageContainer>
        {renderNavBar()}
        <Container>
          <PageHeader>
            <PageTitle>{language === 'tr' ? 'Sepetiniz' : 'Your Cart'}</PageTitle>
            <PageSubtitle>{language === 'tr' ? 'Sepetinizde ürün bulunmuyor' : 'Your cart is empty'}</PageSubtitle>
          </PageHeader>
          <EmptyCart>
            <EmptyCartTitle>{language === 'tr' ? 'Sepetiniz Boş' : 'Your Cart is Empty'}</EmptyCartTitle>
            <EmptyCartText>
              {language === 'tr' ? 'Sepetinize henüz ürün eklemediniz. Alışverişe başlamak için aşağıdaki butona tıklayabilirsiniz.' : 'You haven\'t added any products to your cart yet. Click the button below to start shopping.'}
            </EmptyCartText>
            <ContinueShoppingButton onClick={handleContinueShopping}>
              {language === 'tr' ? 'Alışverişe Başla' : 'Start Shopping'}
            </ContinueShoppingButton>
          </EmptyCart>
        </Container>
      </CartPageContainer>
    );
  }

  return (
    <CartPageContainer>
      <Helmet>
        <title>{language === 'tr' ? 'Sepetiniz - Barkın Sayın Store' : 'Your Cart - Barkın Sayın Store'}</title>
      </Helmet>
      
      {renderNavBar()}
      
      <Container>
        <PageHeader>
          <PageTitle>{language === 'tr' ? 'Sepetiniz' : 'Your Cart'}</PageTitle>
          <PageSubtitle>{language === 'tr' ? 'Sepetinizdeki ürünleri görüntüleyin ve siparişinizi tamamlayın.' : 'View your cart items and complete your order.'}</PageSubtitle>
        </PageHeader>
        
        <CartContent>
          <CartItems>
            {cartItems.map(item => (
              <CartItem key={item.id}>
                <ItemImage src={item.product_image} alt={item.product_name} />
                <ItemDetails>
                  <ItemName>{item.product_name}</ItemName>
                  <ItemPrice>₺{item.price.toLocaleString()}</ItemPrice>
                </ItemDetails>
                <ItemActions>
                  <RemoveButton onClick={() => handleRemoveItem(item.id)}>{language === 'tr' ? 'Kaldır' : 'Remove'}</RemoveButton>
                </ItemActions>
              </CartItem>
            ))}
          </CartItems>
          
          <CartSummary>
            <SummaryTitle>{language === 'tr' ? 'Sipariş Özeti' : 'Order Summary'}</SummaryTitle>
            
            <CargoSection>
              <CargoTitle>{language === 'tr' ? 'Kargo Firması Seçin *' : 'Select Shipping Company *'}</CargoTitle>
              {!selectedCargo && (
                <CargoWarning>
                  {language === 'tr' ? '⚠️ Devam etmek için lütfen bir kargo firması seçin' : '⚠️ Please select a shipping company to continue'}
                </CargoWarning>
              )}
              <CargoOptions>
                <CargoOption 
                  selected={selectedCargo === 'Aras Kargo'}
                  onClick={() => setSelectedCargo('Aras Kargo')}
                >
                  <CargoInfo>
                    <CargoName>Aras Kargo</CargoName>
                    <CargoDelivery>{language === 'tr' ? '1-3 iş günü' : '1-3 business days'}</CargoDelivery>
                  </CargoInfo>
                  <CargoPrice>₺{getCargoPrice('Aras Kargo')}</CargoPrice>
                </CargoOption>
                
                <CargoOption 
                  selected={selectedCargo === 'Sürat Kargo'}
                  onClick={() => setSelectedCargo('Sürat Kargo')}
                >
                  <CargoInfo>
                    <CargoName>Sürat Kargo</CargoName>
                    <CargoDelivery>{language === 'tr' ? '1-3 iş günü' : '1-3 business days'}</CargoDelivery>
                  </CargoInfo>
                  <CargoPrice>₺{getCargoPrice('Sürat Kargo')}</CargoPrice>
                </CargoOption>
                
                <CargoOption 
                  selected={selectedCargo === 'DHL E-COMMERCE'}
                  onClick={() => setSelectedCargo('DHL E-COMMERCE')}
                >
                  <CargoInfo>
                    <CargoName>DHL E-COMMERCE</CargoName>
                    <CargoDelivery>{language === 'tr' ? '1-3 iş günü' : '1-3 business days'}</CargoDelivery>
                  </CargoInfo>
                  <CargoPrice>₺{getCargoPrice('DHL E-COMMERCE')}</CargoPrice>
                </CargoOption>
              </CargoOptions>
            </CargoSection>
            
            <SummaryRow>
              <span>{language === 'tr' ? `Ara Toplam (${cartCount} ürün)` : `Subtotal (${cartCount} item${cartCount > 1 ? 's' : ''})`}</span>
              <span>₺{cartTotal.toLocaleString()}</span>
            </SummaryRow>
            <SummaryRow>
              <span>{language === 'tr' ? 'Kargo' : 'Shipping'}</span>
              <span>{selectedCargo ? `₺${getShippingCost()}` : (language === 'tr' ? 'Seçiniz' : 'Select')}</span>
            </SummaryRow>
            <SummaryTotal>
              <span>{language === 'tr' ? 'Toplam' : 'Total'}</span>
              <span>₺{getTotalWithShipping().toLocaleString()}</span>
            </SummaryTotal>
            <CheckoutButton onClick={handleCheckout} disabled={!selectedCargo}>
              {language === 'tr' ? 'Siparişi Tamamla' : 'Complete Order'}
            </CheckoutButton>
            <ContinueShoppingButton onClick={handleContinueShopping}>
              {language === 'tr' ? 'Alışverişe Devam Et' : 'Continue Shopping'}
            </ContinueShoppingButton>
          </CartSummary>
        </CartContent>
      </Container>
      
      <CheckoutPopup 
        isOpen={isCheckoutPopupOpen}
        onClose={() => setIsCheckoutPopupOpen(false)}
      />
    </CartPageContainer>
  );
};

export default Cart;
